import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { WeatherData } from '../types';
import { reverseGeocodeServer } from '../services/geocodeClient';
import { SunIcon, CloudIcon, RainIcon, WindIcon, DropletIcon } from './icons';

interface WeatherWidgetProps {
  weatherData: WeatherData | null;
  isLoading: boolean;
  tempUnit: 'Celsius' | 'Fahrenheit';
  locationName: string | null;
  coords?: { lat?: number | null; lon?: number | null; lng?: number | null } | null;
  isExpanded?: boolean;
  onExpandedChange?: (open: boolean) => void;
  panelPlacement?: 'self' | 'underSearch' | 'center' | 'anchor-right';
  isDarkMode?: boolean;
}

type WxKind = 'sun' | 'cloud' | 'rain' | 'snow' | 'storm';
type ReadTone = 'good' | 'mixed' | 'tough';
type SignalTone = 'sky' | 'sun' | 'violet' | 'water' | 'green' | 'rose';

type FishingSignalItem = {
  label: string;
  value: string;
  tone?: SignalTone;
  helper?: string;
};

type OverlayBounds = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const rectsOverlap = (a: DOMRect, b: DOMRect) =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const readRoddoOverlayBounds = (): OverlayBounds => {
  const shell = document.getElementById('roddo-app-shell');
  const mapRoot = document.querySelector<HTMLElement>('[data-roddo-tour="big-blue-map"]');
  const header = shell?.querySelector<HTMLElement>('.roddo-final-header') ?? document.querySelector<HTMLElement>('.roddo-final-header');
  const nav = shell?.querySelector<HTMLElement>('.roddo-final-bottom-nav') ?? document.querySelector<HTMLElement>('.roddo-final-bottom-nav');

  const shellRect = shell?.getBoundingClientRect();
  const mapRect = mapRoot?.getBoundingClientRect();
  const headerRect = header?.getBoundingClientRect();
  const navRect = nav?.getBoundingClientRect();

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const baseLeft = mapRect?.left ?? shellRect?.left ?? 0;
  const baseRight = mapRect?.right ?? shellRect?.right ?? viewportWidth;
  const baseTop = mapRect?.top ?? headerRect?.bottom ?? shellRect?.top ?? 0;
  const baseBottom = mapRect?.bottom ?? shellRect?.bottom ?? viewportHeight;

  let left = Math.max(0, Math.round(baseLeft));
  const right = Math.min(viewportWidth, Math.round(baseRight));
  const top = Math.max(0, Math.round(baseTop));
  let bottom = Math.min(viewportHeight, Math.round(baseBottom));

  if (navRect && mapRect && rectsOverlap(navRect, mapRect)) {
    const mapWidth = Math.max(1, mapRect.width);
    const mapHeight = Math.max(1, mapRect.height);
    const isBottomDock =
      navRect.width >= mapWidth * 0.6 &&
      navRect.height <= Math.max(180, mapHeight * 0.38) &&
      navRect.top >= mapRect.top + mapHeight * 0.45;
    const isLeftRail =
      navRect.height >= mapHeight * 0.55 &&
      navRect.width <= Math.max(180, mapWidth * 0.35) &&
      navRect.left <= mapRect.left + Math.max(40, mapWidth * 0.12);

    if (isBottomDock) bottom = Math.min(bottom, Math.round(navRect.top));
    if (isLeftRail) left = Math.max(left, Math.round(navRect.right));
  }

  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
};

const norm = (s?: string) => (s || '').toLowerCase().trim();

const asText = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

const normalizeLocationLabel = (value: unknown): string => {
  const text = asText(value);
  if (!text) return '';
  const low = text.toLowerCase();
  if (low === 'current location' || low === 'current position' || low === 'my location') return '';
  return text;
};

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const s = asText(value);
  const m = s.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
};

const kindFor = (conditionRaw?: string): WxKind => {
  const c = norm(conditionRaw);
  if (c.includes('snow') || c.includes('sleet') || c.includes('blizzard') || c.includes('ice')) return 'snow';
  if (c.includes('storm') || c.includes('thunder')) return 'storm';
  if (c.includes('rain') || c.includes('shower') || c.includes('drizzle')) return 'rain';
  if (c.includes('cloud') || c.includes('overcast') || c.includes('fog') || c.includes('mist')) return 'cloud';
  return 'sun';
};

const iconFor = (kind: WxKind, className: string) => {
  if (kind === 'rain' || kind === 'storm') return <RainIcon className={className} />;
  if (kind === 'cloud' || kind === 'snow') return <CloudIcon className={className} />;
  return <SunIcon className={className} />;
};

const extractMoon = (weatherData: WeatherData | null): string => {
  const wd: any = weatherData || {};
  const cur: any = wd.current || {};
  return asText(wd?.moonPhase || wd?.astronomy?.moonPhase || cur?.moonPhase);
};

const approximateMoonPhase = (date = new Date()): string => {
  // Lightweight fallback used only when the weather payload has no lunar label.
  // Fishing/planning context only; not an astronomy/navigation guarantee.
  const synodicMonth = 29.530588853;
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14, 0);
  const days = (date.getTime() - knownNewMoon) / 86400000;
  const age = ((days % synodicMonth) + synodicMonth) % synodicMonth;

  if (age < 1.84566) return 'New Moon';
  if (age < 5.53699) return 'Waxing Crescent';
  if (age < 9.22831) return 'First Quarter';
  if (age < 12.91963) return 'Waxing Gibbous';
  if (age < 16.61096) return 'Full Moon';
  if (age < 20.30228) return 'Waning Gibbous';
  if (age < 23.99361) return 'Last Quarter';
  if (age < 27.68493) return 'Waning Crescent';
  return 'New Moon';
};

const moonEmoji = (phaseRaw?: string) => {
  const phase = norm(phaseRaw);
  if (phase.includes('new')) return '🌑';
  if (phase.includes('waxing crescent')) return '🌒';
  if (phase.includes('first quarter')) return '🌓';
  if (phase.includes('waxing gibbous')) return '🌔';
  if (phase.includes('full')) return '🌕';
  if (phase.includes('waning gibbous')) return '🌖';
  if (phase.includes('last quarter') || phase.includes('third quarter')) return '🌗';
  if (phase.includes('waning crescent')) return '🌘';
  return '🌙';
};

const extractTides = (weatherData: WeatherData | null) => {
  const wd: any = weatherData || {};
  const cur: any = wd.current || {};
  const tides = wd?.tides || {};

  return {
    mode: asText(tides?.mode || tides?.status || wd?.tideMode),
    stationName: asText(tides?.stationName || tides?.station || wd?.tideStationName),
    nextHigh: asText(tides?.nextHigh || cur?.nextHighTide),
    nextLow: asText(tides?.nextLow || cur?.nextLowTide),
    message: asText(tides?.message || wd?.tideMessage),
    events: Array.isArray(tides?.events) ? tides.events : [],
  };
};

const getBiteScore = (weatherData: WeatherData | null, fallback: number): number => {
  const wd: any = weatherData || {};
  const raw = wd?.fishBiteScore ?? wd?.biteScore ?? wd?.fishing?.biteScore ?? wd?.fishingScore;
  const n = parseNumeric(raw);
  if (typeof n === 'number') return Math.max(0, Math.min(100, Math.round(n)));
  return fallback;
};

const scoreTone = (score: number): ReadTone => {
  if (score >= 72) return 'good';
  if (score <= 42) return 'tough';
  return 'mixed';
};

const scoreLabel = (score: number) => {
  if (score >= 82) return 'Excellent';
  if (score >= 72) return 'Strong';
  if (score >= 58) return 'Decent';
  if (score >= 43) return 'Mixed';
  return 'Tough';
};

const buildTimingSignals = (weatherData: WeatherData | null): FishingSignalItem[] => {
  const wd: any = weatherData || {};
  const cur: any = wd.current || {};
  const sunrise = asText(wd?.sunrise || wd?.astronomy?.sunrise || cur?.sunrise);
  const sunset = asText(wd?.sunset || wd?.astronomy?.sunset || cur?.sunset);
  const moon = extractMoon(weatherData);

  return [
    sunrise ? { label: 'Sunrise', value: sunrise, tone: 'sun', helper: 'Low-light push' } : null,
    sunset ? { label: 'Sunset', value: sunset, tone: 'sun', helper: 'Evening feed' } : null,
    moon ? { label: 'Moon', value: moon, tone: 'violet', helper: 'Lunar bias' } : null,
  ].filter(Boolean) as FishingSignalItem[];
};

const buildBiteWindows = (weatherData: WeatherData | null): FishingSignalItem[] => {
  const wd: any = weatherData || {};
  const raw = wd?.biteWindows || wd?.fishing?.biteWindows;
  if (Array.isArray(raw) && raw.length) {
    return raw.slice(0, 4).map((w: any, idx: number) => ({
      label: asText(w?.type || w?.label || (idx < 2 ? 'Major' : 'Minor')),
      value: asText(w?.window || w?.time || w?.value || 'Timing window'),
      helper: asText(w?.reason || w?.helper || ''),
      tone: norm(w?.type || w?.label).includes('major') ? 'green' : 'sky',
    }));
  }

  const sunrise = asText(wd?.sunrise || wd?.astronomy?.sunrise || wd?.current?.sunrise);
  const sunset = asText(wd?.sunset || wd?.astronomy?.sunset || wd?.current?.sunset);
  return [
    sunrise ? { label: 'Major', value: `${sunrise} ± 90m`, helper: 'Dawn transition', tone: 'green' } : null,
    sunset ? { label: 'Major', value: `${sunset} ± 90m`, helper: 'Dusk transition', tone: 'green' } : null,
    { label: 'Minor', value: 'Midday shade', helper: 'Structure + cover', tone: 'sky' },
    { label: 'Minor', value: 'Night edges', helper: 'Moon/weather dependent', tone: 'violet' },
  ].filter(Boolean) as FishingSignalItem[];
};

const buildWaterSignals = (weatherData: WeatherData | null): FishingSignalItem[] => {
  const tides = extractTides(weatherData);
  const items: FishingSignalItem[] = [];

  if (tides.nextHigh) items.push({ label: 'Next High', value: tides.nextHigh, tone: 'water', helper: tides.stationName });
  if (tides.nextLow) items.push({ label: 'Next Low', value: tides.nextLow, tone: 'water', helper: tides.stationName });

  const marine: any = (weatherData as any)?.marine || {};
  if (asText(marine?.waveHeight)) items.push({ label: 'Swell', value: asText(marine.waveHeight), tone: 'sky', helper: 'Coastal read' });
  if (asText(marine?.waterTemp)) items.push({ label: 'Water', value: asText(marine.waterTemp), tone: 'water', helper: 'Marine temp' });

  return items.slice(0, 4);
};


type ForecastSlice = {
  label: string;
  window: string;
  score: number;
  emoji: string;
  helper: string;
  tone: SignalTone;
};

type WeatherDetailPanel =
  | { kind: 'current' }
  | { kind: 'bite' }
  | { kind: 'forecastWindow'; item: ForecastSlice }
  | { kind: 'minor' }
  | { kind: 'water' }
  | { kind: 'forecastDay'; day: any; index: number }
  | { kind: 'hourly' };

const clampScore = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

const buildForecastSlices = (weatherData: WeatherData | null, baseScore: number): ForecastSlice[] => {
  const wd: any = weatherData || {};
  const cur: any = wd.current || {};
  const condition = asText(cur?.condition || 'Sunny');
  const kind = kindFor(condition);
  const wind = parseNumeric(cur?.wind);
  const humidity = parseNumeric(cur?.humidity);
  const tides = extractTides(weatherData);
  const hasTide = !!(tides.nextHigh || tides.nextLow);

  const slices: ForecastSlice[] = [
    { label: 'Dawn', window: 'First light', score: baseScore + 9, emoji: '🌅', helper: 'Low light + moving fish', tone: 'sun' },
    { label: 'Morning', window: 'Early push', score: baseScore + 4, emoji: '🎣', helper: 'Cover water, then adjust', tone: 'green' },
    { label: 'Midday', window: 'Shade/cover', score: baseScore - 8, emoji: '🌤️', helper: 'Slow down near structure', tone: 'sky' },
    { label: 'Afternoon', window: 'Wind edges', score: baseScore - 2, emoji: '💨', helper: 'Watch ripple + bait', tone: 'water' },
    { label: 'Dusk', window: 'Evening feed', score: baseScore + 10, emoji: '🌇', helper: 'Prime transition window', tone: 'sun' },
    { label: 'Night', window: 'Edges', score: baseScore - 1, emoji: '🌙', helper: 'Quiet banks + silhouettes', tone: 'violet' },
  ];

  return slices.map((item) => {
    let delta = 0;
    let helper = item.helper;

    if (kind === 'cloud') {
      if (item.label === 'Midday' || item.label === 'Afternoon') delta += 7;
      helper = item.label === 'Midday' ? 'Clouds can keep fish shallower' : helper;
    }
    if (kind === 'rain') {
      delta += item.label === 'Morning' || item.label === 'Dusk' ? 5 : 1;
      helper = item.label === 'Morning' ? 'Reaction bites may wake up' : helper;
    }
    if (kind === 'storm' || kind === 'snow') {
      delta -= 18;
      helper = 'Safety first; fish protected water';
    }
    if (kind === 'sun' && item.label === 'Midday') {
      delta -= 7;
      helper = 'Bright sun favors shade/depth';
    }

    if (typeof wind === 'number') {
      if (wind >= 22) {
        delta -= item.label === 'Afternoon' ? 4 : 9;
        helper = 'Pick protected seams';
      } else if (wind >= 8 && wind <= 20 && (item.label === 'Morning' || item.label === 'Afternoon')) {
        delta += 5;
        helper = 'Useful chop can hide you';
      }
    }

    if (typeof humidity === 'number' && humidity >= 75 && (item.label === 'Dawn' || item.label === 'Dusk')) delta += 3;
    if (hasTide && (item.label === 'Morning' || item.label === 'Dusk')) {
      delta += 7;
      helper = 'Match this with tide movement';
    }

    const score = clampScore(item.score + delta);
    const tone: SignalTone = score >= 72 ? 'green' : score <= 42 ? 'rose' : item.tone;
    return { ...item, score, tone, helper };
  });
};

const buildFishIQHints = (weatherData: WeatherData | null): string[] => {
  const wd: any = weatherData || {};
  const candidates = [wd?.fishIQHints, wd?.speciesHints, wd?.fishing?.speciesHints, wd?.fishing?.fishIQHints].find(Array.isArray);
  if (Array.isArray(candidates)) {
    return candidates.map((x: any) => asText(typeof x === 'string' ? x : x?.text || x?.label || x?.species)).filter(Boolean).slice(0, 4);
  }

  const condition = asText(wd?.current?.condition || '');
  const kind = kindFor(condition);
  const moon = norm(extractMoon(weatherData));
  const tides = extractTides(weatherData);

  const hints: string[] = [];
  if (tides.nextHigh || tides.nextLow) hints.push('Coastal species: prioritize current seams, tide turns, points, cuts, and bait movement.');
  if (kind === 'cloud') hints.push('Cloud cover can keep bass, trout, panfish, and inshore predators roaming shallower longer.');
  if (kind === 'rain') hints.push('Rain can favor reaction bites; slow down when visibility drops.');
  if (kind === 'sun') hints.push('Bright sun usually rewards shade, depth changes, docks, grass edges, or low-light timing.');
  if (moon.includes('new') || moon.includes('full')) hints.push('Moon phase may shift feeding windows; trust local FishIQ/catch signals over hard rules.');

  return hints.slice(0, 3);
};

const buildFishingRead = (weatherData: WeatherData | null) => {
  const wd: any = weatherData || {};
  const cur: any = wd.current || {};

  const condition = asText(cur?.condition || 'Sunny');
  const kind = kindFor(condition);
  const wind = parseNumeric(cur?.wind);
  const humidity = parseNumeric(cur?.humidity);
  const moon = norm(extractMoon(weatherData));
  const tides = extractTides(weatherData);

  let rawScore = 58;
  if (kind === 'cloud') rawScore += 13;
  if (kind === 'rain') rawScore += 4;
  if (kind === 'storm' || kind === 'snow') rawScore -= 24;
  if (kind === 'sun') rawScore -= 2;
  if (typeof wind === 'number') {
    if (wind >= 35 || wind >= 22) rawScore -= 16;
    else if (wind >= 10) rawScore += 8;
    else rawScore -= 3;
  }
  if (typeof humidity === 'number' && humidity >= 75 && kind !== 'storm') rawScore += 4;
  if (moon.includes('new') || moon.includes('full')) rawScore += 5;
  if (tides.nextHigh || tides.nextLow) rawScore += 8;

  const score = getBiteScore(weatherData, rawScore);
  const tone = scoreTone(score);

  let headline = `${scoreLabel(score)} bite window`;
  let subline = 'Use this as a planning read, then let local water clarity, pressure, and FishIQ signals decide the final move.';

  if (tides.nextHigh || tides.nextLow) {
    headline = `${scoreLabel(score)} coastal timing`;
    subline = 'Tide movement plus low-light windows is the main edge here. Focus on current seams and bait movement.';
  }

  if (kind === 'storm') {
    headline = 'Safety-first weather window';
    subline = 'Stormy conditions can suppress comfort and consistency. Fish protected water or wait it out.';
  } else if (kind === 'rain') {
    subline = 'Rain can turn fish on, but visibility, runoff, and safe access matter more than usual.';
  } else if (kind === 'cloud') {
    subline = 'Cloud cover can extend roaming and shallow activity, especially around feeding windows.';
  } else if (kind === 'sun') {
    subline = 'Bright conditions usually push the best action toward sunrise, sunset, shade, or structure.';
  }

  if (typeof wind === 'number') {
    if (wind >= 22) {
      headline = tone === 'tough' ? 'Windy, tough window' : 'Windy but workable';
      subline = 'Heavy wind can still help the bite, but pick protected water and fish cleaner edges.';
    } else if (wind >= 10 && tone !== 'tough') {
      headline = `${scoreLabel(score)} ripple window`;
      subline = 'A little chop often helps conceal fish and improves shallow confidence.';
    }
  }

  const chips = [
    `${score}/100`,
    kind === 'storm' ? 'Safety first' : kind === 'rain' ? 'Visibility matters' : kind === 'cloud' ? 'Cloud cover' : 'Low-light bias',
    typeof wind === 'number' ? (wind >= 22 ? 'Windy' : wind >= 10 ? 'Nice ripple' : 'Calmer water') : 'Read the wind',
    tides.nextHigh || tides.nextLow ? 'Tide movement' : 'Inland mode',
  ];

  return { headline, subline, tone, score, chips };
};

const WeatherWidget: React.FC<WeatherWidgetProps> = ({
  weatherData,
  isLoading,
  tempUnit,
  locationName,
  coords,
  isExpanded,
  onExpandedChange,
  panelPlacement = 'self',
  isDarkMode,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelScrollTopRef = useRef(0);
  const [detailPanel, setDetailPanel] = useState<WeatherDetailPanel | null>(null);

  const open = !!isExpanded;
  const tempSymbol = tempUnit === 'Fahrenheit' ? '°F' : '°C';
  const isDark = !!isDarkMode;
  const [resolvedLocationName, setResolvedLocationName] = useState<string>('');
  const [overlayBounds, setOverlayBounds] = useState<OverlayBounds | null>(null);

  const coordLat = typeof coords?.lat === 'number' && Number.isFinite(coords.lat) ? coords.lat : null;
  const coordLonRaw = coords?.lon ?? coords?.lng;
  const coordLon = typeof coordLonRaw === 'number' && Number.isFinite(coordLonRaw) ? coordLonRaw : null;
  const coordKey = coordLat != null && coordLon != null ? `${coordLat.toFixed(4)},${coordLon.toFixed(4)}` : '';

  useLayoutEffect(() => {
    if (!open || (panelPlacement !== 'center' && panelPlacement !== 'anchor-right') || typeof document === 'undefined') return;

    const syncBounds = () => setOverlayBounds(readRoddoOverlayBounds());
    syncBounds();
    const raf = window.requestAnimationFrame(syncBounds);
    window.addEventListener('resize', syncBounds);
    window.visualViewport?.addEventListener('resize', syncBounds);
    window.visualViewport?.addEventListener('scroll', syncBounds);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', syncBounds);
      window.visualViewport?.removeEventListener('resize', syncBounds);
      window.visualViewport?.removeEventListener('scroll', syncBounds);
    };
  }, [open, panelPlacement]);

  const safeForecast = useMemo(() => {
    const raw = (weatherData as any)?.forecast;
    return Array.isArray(raw) ? raw : [];
  }, [weatherData]);

  const safeHourly = useMemo(() => {
    const raw = (weatherData as any)?.hourly;
    return Array.isArray(raw)
      ? raw
          .filter((item: any) => typeof item?.temp === 'number' && Number.isFinite(item.temp))
          .slice(0, 24)
      : [];
  }, [weatherData]);

  const hourlyRange = useMemo(() => {
    if (!safeHourly.length) return { min: 0, max: 1 };
    const values = safeHourly.map((item: any) => Number(item.temp));
    const min = Math.floor(Math.min(...values));
    const max = Math.ceil(Math.max(...values));
    return { min, max: Math.max(min + 1, max) };
  }, [safeHourly]);

  const timingSignals = useMemo(() => buildTimingSignals(weatherData), [weatherData]);
  const biteWindows = useMemo(() => buildBiteWindows(weatherData), [weatherData]);
  const waterSignals = useMemo(() => buildWaterSignals(weatherData), [weatherData]);
  const fishIQHints = useMemo(() => buildFishIQHints(weatherData), [weatherData]);
  const fishingRead = useMemo(() => buildFishingRead(weatherData), [weatherData]);
  const forecastSlices = useMemo(() => buildForecastSlices(weatherData, fishingRead.score), [weatherData, fishingRead.score]);
  const tides = useMemo(() => extractTides(weatherData), [weatherData]);
  const hasUsableWeather = !!weatherData?.current;

  useEffect(() => {
    const directLabel = normalizeLocationLabel(locationName);
    if (directLabel) {
      setResolvedLocationName('');
      return;
    }

    if (!coordKey || coordLat == null || coordLon == null) return;

    let cancelled = false;
    const cacheKey = `roddo.weatherLocationName.${coordKey}`;

    try {
      const cached = normalizeLocationLabel(window.localStorage.getItem(cacheKey));
      if (cached) setResolvedLocationName(cached);
    } catch {
      // localStorage can be unavailable in privacy modes; soft ignore.
    }

    (async () => {
      try {
        const name = normalizeLocationLabel(await reverseGeocodeServer(coordLat, coordLon));
        if (cancelled || !name) return;
        setResolvedLocationName(name);
        try {
          window.localStorage.setItem(cacheKey, name);
        } catch {
          // soft ignore cache write failures
        }
      } catch (err) {
        console.warn('[weather] widget reverse geocode soft fail', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [coordKey, coordLat, coordLon, locationName]);

  const displayLocationName = useMemo(() => {
    const wd: any = weatherData || {};
    const cur: any = wd?.current || {};
    return (
      normalizeLocationLabel(locationName) ||
      normalizeLocationLabel(wd?.locationName) ||
      normalizeLocationLabel(wd?.location) ||
      normalizeLocationLabel(wd?.placeName) ||
      normalizeLocationLabel(wd?.city) ||
      normalizeLocationLabel(wd?.name) ||
      normalizeLocationLabel(cur?.locationName) ||
      normalizeLocationLabel(cur?.location) ||
      normalizeLocationLabel(cur?.city) ||
      normalizeLocationLabel(resolvedLocationName) ||
      ''
    );
  }, [locationName, weatherData, resolvedLocationName]);

  const locationHeaderLabel = displayLocationName || (coordKey ? 'Reading your water…' : 'Finding your water…');

  const current: any = weatherData?.current || {};
  const condition: string = current?.condition ?? 'Sunny';
  const temp = current?.temp ?? '--';
  const feelsLike = current?.feelsLike ?? '--';
  const wind = current?.wind ?? '—';
  const humidity = current?.humidity ?? '—';
  const clouds = asText(current?.clouds || current?.cloudCover || (weatherData as any)?.clouds || (weatherData as any)?.cloudCover);
  const pressure = asText(current?.pressure || (weatherData as any)?.pressure);
  const visibility = asText(current?.visibility || (weatherData as any)?.visibility);
  const precipitation = asText(current?.precipitation || current?.precip || current?.rain || (weatherData as any)?.precipitation);
  const moonPhase = extractMoon(weatherData) || approximateMoonPhase();
  const sunrise = asText((weatherData as any)?.sunrise || (weatherData as any)?.astronomy?.sunrise || current?.sunrise);
  const sunset = asText((weatherData as any)?.sunset || (weatherData as any)?.astronomy?.sunset || current?.sunset);
  const kind = kindFor(condition);

  const ui = isDark
    ? {
        pillBg: 'bg-[#101827]/96',
        pillBorder: 'border-[rgba(132,151,190,0.18)]',
        pillText: 'text-[#F2F6FC]',
        panelBg: 'bg-[#0A1120]/[.985]',
        panelBorder: 'border-[rgba(132,151,190,0.16)]',
        panelText: 'text-[#F4F6FA]',
        title: 'text-[#F4F6FA]',
        sub: 'text-[#AEB8C9]',
        statLabel: 'text-[#7F8BA0]',
        statValue: 'text-[#F4F6FA]',
        tileBg: 'bg-[#101827]',
        tileBorder: 'border-[rgba(132,151,190,0.14)]',
        softBg: 'bg-[#0F1828]',
        softBorder: 'border-[rgba(132,151,190,0.12)]',
        badgeBg: 'bg-[#F2B84B]/12',
        badgeText: 'text-[#FFD98A]',
        divider: 'border-[#9F8CDC]/14',
        iconChip: 'bg-[#151F31]',
        searchRing: 'shadow-[0_4px_12px_rgba(0,0,0,0.18)]',
        goodRead: 'from-[#66D6A9]/14 to-[#66D9E8]/8 border-[#66D6A9]/16',
        mixedRead: 'from-[#F2B84B]/12 to-[#8A70F2]/8 border-[#F2B84B]/14',
        toughRead: 'from-[#EF6A73]/12 to-[#EF6A73]/6 border-[#EF6A73]/14',
        scoreTrack: 'bg-white/10',
        scoreFill: 'bg-[#F2B84B]',
      }
    : {
        pillBg: 'bg-[rgba(255,249,239,0.96)]',
        pillBorder: 'border-[rgba(111,87,60,0.14)]',
        pillText: 'text-[#2E3A49]',
        panelBg: 'bg-[linear-gradient(180deg,rgba(255,249,239,0.995),rgba(247,238,227,0.995))]',
        panelBorder: 'border-[#dfd0bd]',
        panelText: 'text-[#283543]',
        title: 'text-[#22313A]',
        sub: 'text-[#6B6258]',
        statLabel: 'text-[#766E63]',
        statValue: 'text-[#22313A]',
        tileBg: 'bg-[#fff8ee]',
        tileBorder: 'border-[#ece2d2]',
        softBg: 'bg-[#fffaf2]/78',
        softBorder: 'border-[rgba(111,87,60,0.11)]',
        badgeBg: 'bg-[#f7e2ad]',
        badgeText: 'text-[#8c5a00]',
        divider: 'border-black/10',
        iconChip: 'bg-[#f3e7d8]',
        searchRing: 'shadow-[0_12px_28px_rgba(89,65,37,0.12),inset_0_1px_0_rgba(255,255,255,0.92)]',
        goodRead: 'from-emerald-100 to-sky-100 border-emerald-200/80',
        mixedRead: 'from-yellow-100 to-violet-100 border-yellow-200/80',
        toughRead: 'from-rose-100 to-orange-100 border-rose-200/80',
        scoreTrack: 'bg-[#e8ddcc]',
        scoreFill: 'bg-[#6fbf73]',
      };

  const iconClass =
    kind === 'rain' || kind === 'storm'
      ? isDark
        ? 'text-sky-200'
        : 'text-blue-700'
      : kind === 'cloud' || kind === 'snow'
        ? isDark
          ? 'text-indigo-100'
          : 'text-indigo-700'
        : isDark
          ? 'text-yellow-200'
          : 'text-amber-600';

  const signalToneClasses = (tone?: SignalTone) => {
    if (tone === 'water') return isDark ? 'bg-[#66D9E8]/10 border-[#66D9E8]/16 text-[#D5F8FC]' : 'bg-sky-50 border-sky-200 text-sky-900';
    if (tone === 'sun') return isDark ? 'bg-[#F2B84B]/10 border-[#F2B84B]/16 text-[#FFE5AF]' : 'bg-amber-50 border-amber-200 text-amber-900';
    if (tone === 'violet') return isDark ? 'bg-[#8A70F2]/10 border-[#8A70F2]/16 text-[#D9D2FF]' : 'bg-violet-50 border-violet-200 text-violet-900';
    if (tone === 'green') return isDark ? 'bg-[#66D6A9]/10 border-[#66D6A9]/16 text-[#CFF8E7]' : 'bg-emerald-50 border-emerald-200 text-emerald-900';
    if (tone === 'rose') return isDark ? 'bg-[#EF6A73]/10 border-[#EF6A73]/16 text-[#FFD8DC]' : 'bg-rose-50 border-rose-200 text-rose-900';
    return isDark ? 'bg-white/8 border-white/10 text-[#F4F6FA]' : 'bg-[#fffaf2] border-[#ece2d2] text-[#2e271f]';
  };

  useEffect(() => {
    if (!open || panelPlacement === 'center') return;
    const onClickOutside = (e: PointerEvent) => {
      const target = e.target as Node;
      const hitPill = rootRef.current?.contains(target);
      const hitPanel = panelRef.current?.contains(target);
      if (!hitPill && !hitPanel) onExpandedChange?.(false);
    };
    document.addEventListener('pointerdown', onClickOutside);
    return () => document.removeEventListener('pointerdown', onClickOutside);
  }, [open, onExpandedChange, panelPlacement]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (detailPanel) {
        setDetailPanel(null);
        return;
      }
      onExpandedChange?.(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, detailPanel, onExpandedChange]);

  useEffect(() => {
    if (!open) {
      panelScrollTopRef.current = 0;
      setDetailPanel(null);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    const y = detailPanel ? 0 : panelScrollTopRef.current;
    if (y <= 0) {
      panelRef.current.scrollTop = 0;
      return;
    }

    const raf = window.requestAnimationFrame(() => {
      if (panelRef.current) panelRef.current.scrollTop = y;
    });

    return () => window.cancelAnimationFrame(raf);
  }, [open, isLoading, weatherData, panelPlacement, detailPanel]);

  const handlePanelScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!detailPanel) panelScrollTopRef.current = event.currentTarget.scrollTop;
  };

  const openDetail = (next: WeatherDetailPanel) => {
    panelScrollTopRef.current = panelRef.current?.scrollTop || panelScrollTopRef.current;
    setDetailPanel(next);
  };

  const pillClass = [
    'select-none touch-manipulation h-11 w-11 rounded-[14px] flex flex-col items-center justify-center border',
    'transition-[transform,filter,box-shadow] duration-150 ease-out hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.97]',
    ui.pillBg,
    ui.pillBorder,
    ui.searchRing,
  ].join(' ');

  const panelCard = [
    'rounded-[20px] border p-3 shadow-[0_8px_22px_rgba(0,0,0,0.20)] sm:p-3.5',
    ui.panelBg,
    ui.panelBorder,
    ui.panelText,
  ].join(' ');

  const detailButtonClass = [
    'group w-full text-left rounded-[16px] border transition-transform active:scale-[0.985] focus:outline-none focus:ring-2 focus:ring-[#67D8E8]/35',
    'touch-manipulation',
  ].join(' ');

  const readToneClass =
    fishingRead.tone === 'good'
      ? ui.goodRead
      : fishingRead.tone === 'tough'
        ? ui.toughRead
        : ui.mixedRead;

  const shouldPortalPanel = panelPlacement === 'underSearch' || panelPlacement === 'center' || panelPlacement === 'anchor-right';

  const renderPanelShell = (children: React.ReactNode) => {
    if (panelPlacement === 'center' || panelPlacement === 'anchor-right') {
      if (!overlayBounds || overlayBounds.width <= 0 || overlayBounds.height <= 0) return null;

      const desktopAnchored =
        panelPlacement === 'anchor-right' &&
        typeof window !== 'undefined' &&
        window.innerWidth >= 1024 &&
        !!rootRef.current;

      if (desktopAnchored) {
        const anchorRect = rootRef.current!.getBoundingClientRect();
        const gap = 12;
        const pad = 14;
        const panelWidth = Math.min(420, Math.max(300, overlayBounds.width - pad * 2));
        const minLeft = overlayBounds.left + pad;
        const maxLeft = overlayBounds.left + overlayBounds.width - panelWidth - pad;
        const left = Math.round(Math.max(minLeft, Math.min(maxLeft, anchorRect.right + gap)));
        const top = Math.round(Math.max(overlayBounds.top + pad, anchorRect.top - 4));
        const maxHeight = Math.max(240, overlayBounds.top + overlayBounds.height - top - pad);

        return (
          <div
            className='fixed z-[6600] pointer-events-none'
            style={{ top, left, width: panelWidth }}
            role='presentation'
          >
            <div
              ref={panelRef}
              onScroll={handlePanelScroll}
              onPointerDown={(event) => event.stopPropagation()}
              className='pointer-events-auto relative w-full min-h-0 overflow-y-auto overscroll-contain rounded-[28px] px-0'
              style={{
                maxHeight: `${maxHeight}px`,
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {children}
            </div>
          </div>
        );
      }

      return (
        <div
          className='fixed z-[2200] flex items-center justify-center'
          style={{
            top: overlayBounds.top,
            left: overlayBounds.left,
            width: overlayBounds.width,
            height: overlayBounds.height,
            padding: '14px',
            background: isDark ? 'rgba(2,8,18,0.42)' : 'rgba(34,27,20,0.22)',
          }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) onExpandedChange?.(false);
          }}
          role='presentation'
        >
          <div
            ref={panelRef}
            onScroll={handlePanelScroll}
            onPointerDown={(event) => event.stopPropagation()}
            className='relative w-[min(94vw,420px)] min-h-0 overflow-y-auto overscroll-contain rounded-[28px] px-0 sm:w-[min(92vw,420px)]'
            style={{
              maxHeight: 'calc(100% - 4px)',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {children}
          </div>
        </div>
      );
    }

    if (panelPlacement === 'underSearch') {
      return (
        <div
          ref={panelRef}
          onScroll={handlePanelScroll}
          className='fixed left-1/2 z-[1400] w-[min(420px,calc(100vw-20px))] -translate-x-1/2 overflow-y-auto overscroll-contain rounded-[28px] px-0 sm:w-[min(420px,calc(100vw-28px))]'
          style={{
            top: 'calc(var(--roddo-header-h, 88px) + 10px)',
            bottom: 'var(--roddo-map-panel-bottom, calc(var(--roddo-nav-h, 72px) + env(safe-area-inset-bottom) + 14px))',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      );
    }

    return (
      <div
        ref={panelRef}
        onScroll={handlePanelScroll}
        className='absolute left-0 top-[68px] z-[200] w-[min(420px,calc(100vw-20px))] max-h-[min(700px,calc(100svh-170px))] overflow-y-auto overscroll-contain rounded-[28px] sm:w-[min(420px,calc(100vw-28px))]'
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </div>
    );
  };

  const renderPanel = (node: React.ReactNode) =>
    shouldPortalPanel && typeof document !== 'undefined' ? createPortal(node, document.body) : node;

  const sectionTitleClass = `inline-flex min-w-0 items-center gap-1.5 px-0.5 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] sm:text-[11px] sm:tracking-[0.14em] ${isDark ? 'text-[#A8B4C7]' : 'text-[#68523d]'}`;
  const stationLine = tides.stationName ? `Station: ${tides.stationName}` : tides.message;

  const sourceNote = (weatherData as any)?.source || (weatherData as any)?.provider || 'Roddo weather read from available current, forecast, tide, and FishIQ inputs.';
  const fetchedAt = Number((weatherData as any)?.fetchedAt || 0);
  const freshnessLabel = fetchedAt
    ? `Updated ${new Date(fetchedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : '';

  const cloudLine = clouds || 'Not reported';
  const pressureLine = pressure || 'Not reported';
  const visibilityLine = visibility || 'Not reported';
  const precipLine = precipitation || 'Not reported';

  const emojiForLabel = (labelRaw: string) => {
    const label = norm(labelRaw);
    if (label.includes('wind')) return '💨';
    if (label.includes('humidity')) return '💧';
    if (label.includes('cloud')) return '☁️';
    if (label.includes('pressure')) return '🧭';
    if (label.includes('visibility')) return '👀';
    if (label.includes('precip') || label.includes('rain')) return '🌧️';
    if (label.includes('weather')) return kind === 'rain' ? '🌧️' : kind === 'cloud' ? '☁️' : kind === 'storm' ? '⛈️' : '🌤️';
    if (label.includes('source')) return '📡';
    if (label.includes('bite')) return '🐟';
    if (label.includes('tactic') || label.includes('best use')) return '🎣';
    if (label.includes('score') || label.includes('outlook')) return '📈';
    if (label.includes('moon')) return '🌙';
    if (label.includes('tide') || label.includes('water') || label.includes('swell') || label.includes('high') || label.includes('low')) return '🌊';
    if (label.includes('sunrise') || label.includes('dawn')) return '🌅';
    if (label.includes('sunset') || label.includes('dusk')) return '🌇';
    if (label.includes('light')) return '✨';
    if (label.includes('local') || label.includes('structure') || label.includes('cover')) return '📍';
    if (label.includes('planning') || label.includes('trip')) return '🗺️';
    if (label.includes('range') || label.includes('temp')) return '🌡️';
    if (label.includes('change') || label.includes('pocket')) return '🔄';
    if (label.includes('station')) return '📍';
    return '✦';
  };

  const StatRow = ({ label, value, helper, emoji }: { label: string; value: React.ReactNode; helper?: string; emoji?: string }) => {
    const displayEmoji = emoji || emojiForLabel(label);

    return (
      <div className={`group relative overflow-hidden rounded-[18px] border px-3 py-2.5 ${ui.softBg} ${ui.softBorder}`}>
        <div className='pointer-events-none absolute -right-2 -top-3 text-[38px] leading-none opacity-[0.10] transition-opacity group-hover:opacity-[0.16]'>
          {displayEmoji}
        </div>
        <div className='relative flex items-start gap-2.5'>
          <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-2xl border text-[16px] shadow-sm ${ui.iconChip} ${ui.softBorder}`}>
            {displayEmoji}
          </div>
          <div className='min-w-0 flex-1'>
            <div className={`text-[10px] font-black uppercase tracking-[0.12em] ${ui.statLabel}`}>{label}</div>
            <div className={`mt-1 text-[15px] font-black leading-tight ${ui.statValue}`}>{value}</div>
            {helper ? <div className={`mt-1 text-[11px] font-bold leading-4 ${ui.sub}`}>{helper}</div> : null}
          </div>
        </div>
      </div>
    );
  };

  const DetailHeader = ({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) => (
    <div className='flex items-start justify-between gap-3'>
      <div className='min-w-0'>
        <div className={sectionTitleClass}>{eyebrow}</div>
        <h3 className={`mt-2 text-[22px] font-black leading-tight ${ui.title}`}>{title}</h3>
        {subtitle ? <p className={`mt-1 text-[13px] font-bold leading-5 ${ui.sub}`}>{subtitle}</p> : null}
      </div>
      <button
        type='button'
        aria-label='Back to weather overview'
        title='Back'
        onClick={() => setDetailPanel(null)}
        className={`shrink-0 grid h-9 w-9 place-items-center rounded-full border text-[18px] font-black leading-none transition-transform active:scale-95 ${ui.softBg} ${ui.softBorder} ${ui.sub}`}
      >
        ×
      </button>
    </div>
  );

  const renderTideChart = (compact = false) =>
    Array.isArray(tides.events) && tides.events.length > 0 ? (
      <div className={`rounded-2xl border p-2.5 ${ui.softBg} ${ui.softBorder}`}>
        <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.12em] ${ui.sub}`}><span>🌊</span><span>Tide chart</span></div>
        <div className={`mt-2 flex items-end gap-1.5 ${compact ? 'min-h-[48px]' : 'min-h-[68px]'}`}>
          {tides.events.slice(0, compact ? 6 : 8).map((ev: any, idx: number) => {
            const isHigh = norm(ev?.type).startsWith('h');
            return (
              <div key={`${ev?.time || idx}-${ev?.type || idx}`} className='flex-1 text-center'>
                <div className={`mx-auto rounded-t-xl ${isHigh ? (compact ? 'h-10' : 'h-14') : (compact ? 'h-5' : 'h-7')} ${isDark ? 'bg-sky-300/45' : 'bg-sky-300'}`} />
                <div className={`mt-1 text-[9px] font-black ${ui.statLabel}`}>{isHigh ? 'H' : 'L'}</div>
                <div className={`text-[9px] font-bold ${ui.statLabel}`}>{asText(ev?.time).replace(/\s+/g, ' ')}</div>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  const tacticForScore = (score: number) => {
    if (kind === 'storm') return 'Safety first: skip exposed water, avoid lightning risk, and wait for a calmer window.';
    if (score >= 72) return 'Cover water confidently. Start with moving baits or active presentations, then slow down once fish show themselves.';
    if (score <= 42) return 'Go finesse and precise. Fish shade, depth changes, protected seams, docks, grass edges, or slower bottom contact.';
    return 'Start flexible. Test moving baits early, then switch to slower structure work if the first pass is quiet.';
  };

  const renderDetailPanel = () => {
    if (!detailPanel) return null;

    if (detailPanel.kind === 'current') {
      return (
        <div className='space-y-3'>
          <DetailHeader
            eyebrow='Current Weather'
            title={`${temp}${tempSymbol} · ${condition}`}
            subtitle='A cleaner read of what the weather is doing right now and how much confidence to put into it.'
          />
          <div className={`rounded-[24px] border p-3 ${ui.tileBg} ${ui.tileBorder}`}>
            <div className='flex items-center gap-3'>
              <div className={`grid h-16 w-16 place-items-center rounded-[22px] border ${ui.iconChip} ${ui.tileBorder}`}>
                {iconFor(kind, `w-9 h-9 ${iconClass}`)}
              </div>
              <div className='min-w-0'>
                <div className={`text-[44px] font-black leading-none tracking-[-0.04em] ${ui.title}`}>{temp}{tempSymbol}</div>
                <div className={`mt-1 text-[13px] font-black ${ui.sub}`}>Feels like {feelsLike}{tempSymbol}</div>
              </div>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <StatRow label='Wind' value={wind} helper='Ripple, drift, and casting control.' />
            <StatRow label='Humidity' value={humidity} helper='Can support dawn/dusk comfort and activity.' />
            <StatRow label='Clouds' value={cloudLine} helper='Cloud cover often extends shallow roaming.' />
            <StatRow label='Pressure' value={pressureLine} helper='Stable/change pockets can matter more than exact value.' />
            <StatRow label='Visibility' value={visibilityLine} helper='Low visibility favors vibration, contrast, and slower reads.' />
            <StatRow label='Precip' value={precipLine} helper='Rain/runoff can help or hurt depending on clarity.' />
          </div>
          <div className={`relative overflow-hidden rounded-[20px] border px-3 py-3 ${ui.softBg} ${ui.softBorder}`}>
            <div className='pointer-events-none absolute -right-2 -top-3 text-[42px] opacity-[0.10]'>📡</div>
            <div className='relative flex gap-2.5'>
              <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-2xl border text-[16px] shadow-sm ${ui.iconChip} ${ui.softBorder}`}>📡</div>
              <div className={`text-[12px] font-bold leading-5 ${ui.sub}`}>
                <span className={`font-black ${ui.statValue}`}>Source notes:</span> {sourceNote}{freshnessLabel ? ` · ${freshnessLabel}` : ''}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (detailPanel.kind === 'hourly') {
      return (
        <div className='space-y-3'>
          <DetailHeader
            eyebrow='Hourly Weather'
            title='Next 24 hours'
            subtitle='Temperature, precipitation chance, wind, humidity, and conditions from the same live weather response.'
          />

          {safeHourly.length ? (
            <>
              <div className={`rounded-[24px] border p-3 ${ui.tileBg} ${ui.tileBorder}`}>
                <div className='flex h-[150px] items-end gap-1.5 overflow-x-auto pb-1'>
                  {safeHourly.map((hour: any, index: number) => {
                    const span = Math.max(1, hourlyRange.max - hourlyRange.min);
                    const pct = 24 + ((Number(hour.temp) - hourlyRange.min) / span) * 68;
                    return (
                      <div
                        key={`${hour.time || hour.label}-${index}`}
                        className='flex min-w-[42px] flex-1 flex-col items-center justify-end'
                        title={`${hour.label}: ${hour.temp}${tempSymbol}`}
                      >
                        <div className={`mb-1 text-[10px] font-black ${ui.statValue}`}>
                          {Math.round(Number(hour.temp))}°
                        </div>
                        <div
                          className={`w-5 rounded-t-full ${isDark ? 'bg-sky-300/70' : 'bg-sky-400/70'}`}
                          style={{ height: `${pct}%` }}
                        />
                        <div className={`mt-1 text-[9px] font-black ${ui.statLabel}`}>
                          {hour.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className={`mt-2 flex items-center justify-between text-[10px] font-bold ${ui.sub}`}>
                  <span>Low {hourlyRange.min}{tempSymbol}</span>
                  <span>High {hourlyRange.max}{tempSymbol}</span>
                </div>
              </div>

              <div className='space-y-2'>
                {safeHourly.slice(0, 12).map((hour: any, index: number) => (
                  <div
                    key={`${hour.time || hour.label}-row-${index}`}
                    className={`grid grid-cols-[54px_1fr_auto] items-center gap-2 rounded-[18px] border px-3 py-2 ${ui.softBg} ${ui.softBorder}`}
                  >
                    <div className={`text-[11px] font-black ${ui.statValue}`}>{hour.label}</div>
                    <div className='min-w-0'>
                      <div className={`truncate text-[12px] font-black ${ui.statValue}`}>{hour.condition}</div>
                      <div className={`mt-0.5 text-[10px] font-bold ${ui.sub}`}>
                        Rain {hour.precipitationProbability ?? 0}% · Wind {hour.windKmh ?? '—'} km/h · Humidity {hour.humidity ?? '—'}%
                      </div>
                    </div>
                    <div className={`text-[16px] font-black ${ui.statValue}`}>
                      {Math.round(Number(hour.temp))}{tempSymbol}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={`rounded-[20px] border px-3 py-4 text-sm font-bold ${ui.softBg} ${ui.softBorder} ${ui.sub}`}>
              Hourly weather is not available yet. Refresh the weather read and try again.
            </div>
          )}
        </div>
      );
    }

    if (detailPanel.kind === 'bite') {
      const detailRows = [
        { label: 'Weather bias', value: condition, helper: kind === 'cloud' ? 'Cloud cover can keep fish active shallower longer.' : kind === 'sun' ? 'Bright sun usually pushes fish to shade, depth, or low-light periods.' : kind === 'rain' ? 'Rain can wake up reaction bites, but clarity and safe access matter.' : kind === 'storm' ? 'Storm risk overrides bite potential.' : 'Use local cover and water clarity to verify.' },
        { label: 'Wind read', value: wind, helper: parseNumeric(wind) && Number(parseNumeric(wind)) >= 22 ? 'Heavy wind: pick protected seams and cleaner casting lanes.' : 'Moderate ripple can hide you and move bait.' },
        { label: 'Moon/tide help', value: tides.nextHigh || tides.nextLow ? 'Tide movement' : (extractMoon(weatherData) || 'Inland mode'), helper: tides.nextHigh || tides.nextLow ? 'Match your session to moving water, current seams, and bait.' : 'Inland score leans on weather, light, cover, and FishIQ hints.' },
      ];

      return (
        <div className='space-y-3'>
          <DetailHeader
            eyebrow='Fish Bite Activity'
            title={fishingRead.headline}
            subtitle={fishingRead.subline}
          />
          <div className={`rounded-[24px] border bg-gradient-to-br p-3 ${readToneClass}`}>
            <div className='flex items-end justify-between gap-3'>
              <div>
                <div className={`text-[54px] font-black leading-none ${ui.title}`}>{fishingRead.score}</div>
                <div className={`text-[11px] font-black uppercase tracking-[0.12em] ${ui.sub}`}>Bite score / 100</div>
              </div>
              <div className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.10em] ${ui.softBg} ${ui.softBorder}`}>
                {scoreLabel(fishingRead.score)}
              </div>
            </div>
            <div className={`mt-3 h-3 rounded-full overflow-hidden ${ui.scoreTrack}`}>
              <div className={`h-full rounded-full ${ui.scoreFill}`} style={{ width: `${Math.max(5, Math.min(100, fishingRead.score))}%` }} />
            </div>
          </div>
          <div className='grid gap-2'>
            {detailRows.map((row) => (
              <StatRow key={row.label} label={row.label} value={row.value} helper={row.helper} />
            ))}
          </div>
          <div>
            <div className={`text-[10px] font-black uppercase tracking-[0.12em] ${ui.statLabel}`}>Chips explained</div>
            <div className='mt-2 flex flex-wrap gap-2'>
              {fishingRead.chips.map((chip) => (
                <span key={chip} className={`rounded-full border px-2.5 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] ${ui.softBg} ${ui.softBorder}`}>
                  {chip}
                </span>
              ))}
            </div>
          </div>
          <div className={`relative overflow-hidden rounded-[20px] border px-3 py-3 text-[13px] font-black leading-5 ${signalToneClasses(fishingRead.tone === 'good' ? 'green' : fishingRead.tone === 'tough' ? 'rose' : 'sun')}`}>
            <div className='pointer-events-none absolute -right-2 -top-4 text-[46px] opacity-[0.14]'>🎣</div>
            <div className='relative flex gap-2.5'>
              <span className='text-[18px] leading-5'>🎣</span>
              <span>Suggested tactic: {tacticForScore(fishingRead.score)}</span>
            </div>
          </div>
        </div>
      );
    }

    if (detailPanel.kind === 'forecastWindow') {
      const item = detailPanel.item;
      return (
        <div className='space-y-3'>
          <DetailHeader
            eyebrow='FishIQ Forecast'
            title={`${item.emoji} ${item.label}: ${item.window}`}
            subtitle='A tactical window read based on weather, light, wind, humidity, and tide availability.'
          />
          <div className={`rounded-[24px] border px-3 py-3 ${signalToneClasses(item.tone)}`}>
            <div className='flex items-end justify-between gap-3'>
              <div>
                <div className='text-[46px] font-black leading-none'>{item.score}</div>
                <div className='text-[10px] font-black uppercase tracking-[0.12em] opacity-75'>window score</div>
              </div>
              <span className='text-[42px] leading-none'>{item.emoji}</span>
            </div>
            <div className='mt-3 h-2.5 overflow-hidden rounded-full bg-black/10'>
              <div className='h-full rounded-full bg-current opacity-60' style={{ width: `${Math.max(7, item.score)}%` }} />
            </div>
            <p className='mt-3 text-[13px] font-extrabold leading-5 opacity-80'>{item.helper}</p>
          </div>
          <div className='grid gap-2'>
            <StatRow label='Best use' value={item.score >= 72 ? 'Priority window' : item.score <= 42 ? 'Backup window' : 'Playable window'} helper={tacticForScore(item.score)} />
            <StatRow label='Light logic' value={item.label === 'Dawn' || item.label === 'Dusk' ? 'Low light edge' : item.label === 'Midday' ? 'Shade/cover bias' : 'Adjust with wind'} helper='FishIQ windows are soft planning signals, not guarantees.' />
            <StatRow label='Local check' value={tides.nextHigh || tides.nextLow ? 'Match tide movement' : 'Watch structure + bait'} helper='Use visible bait, water clarity, and recent catches to confirm.' />
          </div>
        </div>
      );
    }

    if (detailPanel.kind === 'minor') {
      return (
        <div className='space-y-3'>
          <DetailHeader
            eyebrow='Minor Factors'
            title='Moon, pressure, wind, and light logic'
            subtitle='The small signals that can turn a decent plan into a better one.'
          />
          {timingSignals.length > 0 ? (
            <div className='grid grid-cols-2 gap-2'>
              {timingSignals.map((item) => (
                <StatRow key={item.label} label={item.label} value={item.value} helper={item.helper} />
              ))}
            </div>
          ) : null}
          <div className='grid gap-2'>
            <StatRow label='Moon phase' value={`${moonEmoji(moonPhase)} ${moonPhase}`} helper='Use lunar phase as a soft timing cue; local FishIQ and water conditions stay more important.' />
            <StatRow label='Weather-change pockets' value={kind === 'storm' ? 'Safety-first' : kind === 'rain' ? 'Rain/runoff watch' : kind === 'cloud' ? 'Extended roam' : 'Shade/depth bias'} helper='Recent or incoming changes can matter more than the current snapshot.' />
            <StatRow label='Pressure logic' value={pressureLine} helper='Stable pressure is usually easier to pattern; fast changes can scatter behavior.' />
            <StatRow label='Wind/light logic' value={`${wind} · ${condition}`} helper='Ripple and lower light can make fish less spooky. Bright calm water asks for stealth.' />
          </div>
          {biteWindows.length > 0 ? (
            <div>
              <div className={`text-[10px] font-black uppercase tracking-[0.12em] ${ui.statLabel}`}>Soft windows</div>
              <div className='mt-2 grid grid-cols-2 gap-2'>
                {biteWindows.map((item, idx) => (
                  <div key={`${item.label}-${item.value}-${idx}`} className={`relative overflow-hidden rounded-[18px] border px-3 py-2.5 ${signalToneClasses(item.tone)}`}>
                    <div className='pointer-events-none absolute -right-1 -top-2 text-[30px] opacity-[0.16]'>{emojiForLabel(item.label)}</div>
                    <div className='relative flex items-center gap-2'>
                      <span className='text-[15px]'>{emojiForLabel(item.label)}</span>
                      <div className='text-[10px] font-black uppercase tracking-[0.08em] opacity-75'>{item.label}</div>
                    </div>
                    <div className='relative mt-1 text-[13px] font-black'>{item.value}</div>
                    {item.helper ? <div className='relative mt-1 text-[10.5px] font-extrabold opacity-70'>{item.helper}</div> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    if (detailPanel.kind === 'water') {
      return (
        <div className='space-y-3'>
          <DetailHeader
            eyebrow='Water Timing'
            title={waterSignals.length > 0 ? 'Tides + water movement' : 'Inland timing mode'}
            subtitle={waterSignals.length > 0 ? 'Use tide turns and water movement to pick where fish can ambush bait.' : 'No tide feed is available here, so Roddo leans on weather, light, moon, and FishIQ signals.'}
          />
          {waterSignals.length > 0 ? (
            <div className='grid grid-cols-2 gap-2'>
              {waterSignals.map((item) => (
                <StatRow key={`${item.label}-${item.value}`} label={item.label} value={item.value} helper={item.helper} />
              ))}
            </div>
          ) : (
            <div className={`relative overflow-hidden rounded-[20px] border px-3 py-3 ${signalToneClasses('sky')}`}>
              <div className='pointer-events-none absolute -right-2 -top-4 text-[46px] opacity-[0.14]'>🏞️</div>
              <div className='relative flex items-center gap-2'>
                <span className='text-[18px]'>🏞️</span>
                <div className='text-[11px] font-black uppercase tracking-[0.08em] opacity-75'>Water timing</div>
              </div>
              <div className='relative mt-1 text-[14px] font-black'>Inland conditions mode</div>
              <div className='relative mt-1 text-[11px] font-extrabold opacity-70'>Using moon, weather, and FishIQ signals instead.</div>
            </div>
          )}
          {renderTideChart(false)}
          {stationLine ? <StatRow label='Station' value={stationLine} helper='Use the nearest available gauge as a planning reference.' /> : null}
          <div className={`relative overflow-hidden rounded-[20px] border px-3 py-3 text-[13px] font-black leading-5 ${signalToneClasses('water')}`}>
            <div className='pointer-events-none absolute -right-2 -top-4 text-[46px] opacity-[0.14]'>🐟</div>
            <div className='relative flex gap-2.5'>
              <span className='text-[18px] leading-5'>🐟</span>
              <span>Fishing interpretation: moving water often positions bait and predators. Prioritize current seams, points, cuts, outflows, grass edges, and protected ambush lanes.</span>
            </div>
          </div>
        </div>
      );
    }

    const day = detailPanel.day || {};
    const dayKind = kindFor(day?.condition);
    const dayScore = clampScore(fishingRead.score + (dayKind === 'cloud' ? 6 : dayKind === 'rain' ? 2 : dayKind === 'storm' || dayKind === 'snow' ? -18 : -2) + (detailPanel.index === 0 ? 2 : 0));
    const dayTone: SignalTone = dayScore >= 72 ? 'green' : dayScore <= 42 ? 'rose' : 'sun';

    return (
      <div className='space-y-3'>
        <DetailHeader
          eyebrow='Forecast Day'
          title={`${day?.day || 'Forecast'} · ${day?.condition || 'Weather read'}`}
          subtitle='Trip-planning view for the day card.'
        />
        <div className={`rounded-[24px] border px-3 py-3 ${signalToneClasses(dayTone)}`}>
          <div className='flex items-center justify-between gap-3'>
            <div>
              <div className='text-[34px] font-black leading-none'>{day?.high ?? '--'}{tempSymbol} / {day?.low ?? '--'}{tempSymbol}</div>
              <div className='mt-1 text-[11px] font-black uppercase tracking-[0.12em] opacity-75'>temp range</div>
            </div>
            {iconFor(dayKind, `w-10 h-10 ${dayKind === 'rain' || dayKind === 'storm' ? (isDark ? 'text-sky-200' : 'text-blue-700') : dayKind === 'cloud' || dayKind === 'snow' ? (isDark ? 'text-indigo-100' : 'text-indigo-700') : (isDark ? 'text-yellow-200' : 'text-amber-600')}`)}
          </div>
        </div>
        <div className='grid gap-2'>
          <StatRow label='Bite outlook' value={`${dayScore}/100 · ${scoreLabel(dayScore)}`} helper={tacticForScore(dayScore)} />
          <StatRow label='Weather' value={day?.condition || 'Not reported'} helper={dayKind === 'cloud' ? 'Clouds can improve midday confidence.' : dayKind === 'sun' ? 'Favor sunrise, sunset, shade, or depth.' : dayKind === 'rain' ? 'Watch clarity/runoff and safe access.' : 'Safety and comfort first.'} />
          <StatRow label='Planning note' value={detailPanel.index === 0 ? 'Today' : 'Upcoming'} helper='Use this as a broad trip read, then check the current card before you leave.' />
        </div>
      </div>
    );
  };

  if (isLoading && !hasUsableWeather) {
    const shell = renderPanelShell(
      <div className={panelCard}>
        <div className={`font-black ${ui.title}`}>{locationHeaderLabel}</div>
        <div className={`mt-2 text-sm font-bold ${ui.sub}`}>Loading fishing conditions…</div>
      </div>,
    );

    return (
      <div ref={rootRef} className='relative roddo-weather-polish' data-roddo-polish-lane='2'>
        <button
          type='button'
          onClick={() => onExpandedChange?.(!open)}
          className={pillClass + (open ? ' ring-2 ring-[#2F80ED]/25 dark:ring-[#67D8E8]/25' : '')}
          aria-label='Weather'
          title='Weather'
        >
          <span className='leading-none'><CloudIcon className={`w-5 h-5 ${iconClass}`} /></span>
          <div className={`mt-0.5 text-[10px] font-black leading-none ${ui.pillText}`}>--</div>
        </button>
        {open && renderPanel(shell)}
      </div>
    );
  }

  const noDataPanel = renderPanelShell(
    <div className={panelCard}>
      <div className='flex justify-between items-start gap-3'>
        <div className='min-w-0'>
          <h3 className={`font-black truncate ${ui.title}`}>{locationHeaderLabel}</h3>
          <div className={`mt-2 text-sm font-bold ${ui.sub}`}>{t('weather.unavailable', 'Weather is temporarily unavailable.')}</div>
        </div>
        <button
          type='button'
          aria-label='Close weather details'
          title='Close'
          onClick={() => onExpandedChange?.(false)}
          className={`shrink-0 grid h-8 w-8 place-items-center rounded-full border text-[17px] font-black leading-none ${ui.softBg} ${ui.softBorder} ${ui.sub}`}
        >
          ×
        </button>
      </div>
    </div>,
  );

  if (!weatherData) {
    return (
      <div ref={rootRef} className='relative roddo-weather-polish' data-roddo-polish-lane='2'>
        <button
          type='button'
          onClick={() => onExpandedChange?.(!open)}
          className={pillClass + (open ? ' ring-2 ring-[#2F80ED]/25 dark:ring-[#67D8E8]/25' : '')}
          aria-label='Weather'
          title='Weather'
        >
          <span className='leading-none'><CloudIcon className={`w-5 h-5 ${iconClass}`} /></span>
          <div className={`mt-0.5 text-[10px] font-black leading-none ${ui.pillText}`}>--</div>
        </button>
        {open && renderPanel(noDataPanel)}
      </div>
    );
  }

  const overview = (
    <>
      <div className='flex justify-between items-start gap-3'>
        <div className='min-w-0'>
          <div className={sectionTitleClass}>📍 {locationHeaderLabel}</div>
        </div>
        <button
          type='button'
          aria-label='Close weather details'
          title='Close'
          onClick={() => onExpandedChange?.(false)}
          className={`shrink-0 grid h-9 w-9 place-items-center rounded-full border text-[18px] font-black leading-none transition-transform active:scale-95 ${ui.softBg} ${ui.softBorder} ${ui.sub}`}
        >
          ×
        </button>
      </div>

      <div className='mt-3 space-y-3'>
        <button
          type='button'
          onClick={() => openDetail({ kind: 'current' })}
          className={`${detailButtonClass} flex items-center justify-between gap-3 p-3 sm:p-3.5 ${ui.tileBg} ${ui.tileBorder}`}
        >
          <div className='min-w-0'>
            <div className={`mb-2 inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 ${ui.badgeBg} ${ui.softBorder}`}>
              {iconFor(kind, `w-5 h-5 ${iconClass}`)}
              <span className={`truncate text-[12px] font-black ${ui.badgeText}`}>{condition}</span>
            </div>
            <div className={`text-[42px] font-black leading-none tracking-[-0.04em] sm:text-[48px] ${ui.title}`}>{temp}{tempSymbol}</div>
            <div className={`mt-1 text-[14px] font-black ${ui.sub}`}>Feels like {feelsLike}{tempSymbol}</div>
          </div>

          <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-[20px] border sm:h-16 sm:w-16 sm:rounded-[22px] ${ui.iconChip} ${ui.tileBorder}`}>
            {iconFor(kind, `w-9 h-9 ${iconClass}`)}
          </div>
        </button>

        {safeHourly.length > 0 ? (
          <button
            type='button'
            onClick={() => openDetail({ kind: 'hourly' })}
            className={`${detailButtonClass} p-3 ${ui.softBg} ${ui.softBorder}`}
          >
            <div className='flex items-center justify-between gap-3'>
              <div className={sectionTitleClass}>🕐 Hourly Weather</div>
              <span className={`text-[10px] font-black ${ui.sub}`}>Next 24h</span>
            </div>
            <div className='mt-2 flex gap-1.5 overflow-x-auto pb-1 sm:gap-2'>
              {safeHourly.slice(0, 6).map((hour: any, index: number) => (
                <div
                  key={`${hour.time || hour.label}-preview-${index}`}
                  className={`min-w-[52px] rounded-[15px] border px-1.5 py-2 text-center sm:min-w-[56px] sm:px-2 ${ui.tileBg} ${ui.tileBorder}`}
                >
                  <div className={`text-[9px] font-black ${ui.statLabel}`}>{hour.label}</div>
                  <div className={`mt-1 text-[15px] font-black ${ui.statValue}`}>
                    {Math.round(Number(hour.temp))}°
                  </div>
                  <div className={`mt-0.5 text-[9px] font-bold ${ui.sub}`}>
                    {hour.precipitationProbability ?? 0}% rain
                  </div>
                </div>
              ))}
            </div>
          </button>
        ) : null}

        <button
          type='button'
          onClick={() => openDetail({ kind: 'minor' })}
          className={`${detailButtonClass} p-3 ${ui.softBg} ${ui.softBorder}`}
        >
          <div className='flex items-center justify-between gap-3'>
            <div className={sectionTitleClass}>Conditions</div>
            <span className={`text-[10px] font-black ${ui.sub}`}>At a glance</span>
          </div>

          <div className='mt-2 grid grid-cols-2 gap-2'>
            <div className={`min-w-0 rounded-[16px] border px-3 py-2.5 ${ui.tileBg} ${ui.tileBorder}`}>
              <div className={`text-[9px] font-black uppercase tracking-[0.10em] ${ui.statLabel}`}>Wind</div>
              <div className={`mt-1 text-[14px] font-black ${ui.statValue}`}>{wind}</div>
            </div>

            <div className={`min-w-0 rounded-[16px] border px-3 py-2.5 ${ui.tileBg} ${ui.tileBorder}`}>
              <div className={`text-[9px] font-black uppercase tracking-[0.10em] ${ui.statLabel}`}>Humidity</div>
              <div className={`mt-1 text-[14px] font-black ${ui.statValue}`}>{humidity}</div>
            </div>

            <div className={`min-w-0 rounded-[16px] border px-3 py-2.5 ${ui.tileBg} ${ui.tileBorder}`}>
              <div className={`text-[9px] font-black uppercase tracking-[0.10em] ${ui.statLabel}`}>Moon</div>
              <div className={`mt-1 flex items-center gap-1.5 text-[13px] font-black ${ui.statValue}`}>
                <span className='text-[16px]'>{moonEmoji(moonPhase)}</span>
                <span className='min-w-0 leading-4'>{moonPhase}</span>
              </div>
            </div>

            <div className={`min-w-0 rounded-[16px] border px-3 py-2.5 ${ui.tileBg} ${ui.tileBorder}`}>
              <div className={`text-[9px] font-black uppercase tracking-[0.10em] ${ui.statLabel}`}>Sun</div>
              {sunrise || sunset ? (
                <div className={`mt-1 space-y-0.5 text-[11px] font-black leading-4 ${ui.statValue}`}>
                  {sunrise ? <div className='truncate'>↑ {sunrise}</div> : null}
                  {sunset ? <div className='truncate'>↓ {sunset}</div> : null}
                </div>
              ) : (
                <div className={`mt-1 text-[11px] font-black ${ui.statValue}`}>Daylight timing</div>
              )}
            </div>
          </div>
        </button>

        <button
          type='button'
          onClick={() => openDetail({ kind: 'bite' })}
          className={`${detailButtonClass} bg-gradient-to-br p-3 ${readToneClass}`}
        >
          <div className='flex items-start justify-between gap-3'>
            <div className='min-w-0'>
              <div className={sectionTitleClass}>Fishing Outlook</div>
              <div className={`mt-2 text-[16px] font-black leading-tight sm:text-[18px] ${ui.title}`}>{fishingRead.headline}</div>
            </div>
            <div className='shrink-0 text-right'>
              <div className={`text-[28px] font-black leading-none sm:text-3xl ${ui.title}`}>{fishingRead.score}</div>
              <div className={`text-[9px] font-black uppercase tracking-[0.12em] ${ui.sub}`}>/ 100</div>
            </div>
          </div>
          <div className={`mt-2 h-2 rounded-full overflow-hidden ${ui.scoreTrack}`}>
            <div className={`h-full rounded-full ${ui.scoreFill}`} style={{ width: `${Math.max(5, Math.min(100, fishingRead.score))}%` }} />
          </div>
          <p className={`mt-2 text-[11.5px] font-semibold leading-[1.45] sm:text-[12px] ${ui.sub}`}>{fishingRead.subline}</p>
          <div className='mt-2 flex flex-wrap gap-1.5'>
            {fishingRead.chips.slice(0, 3).map((chip) => (
              <span
                key={chip}
                className={`rounded-full border px-2 py-1 text-[9px] font-black uppercase tracking-[0.07em] ${ui.softBg} ${ui.softBorder}`}
              >
                {chip}
              </span>
            ))}
          </div>
        </button>

        <button
          type='button'
          onClick={() => openDetail({ kind: 'water' })}
          className={`${detailButtonClass} p-3 ${ui.softBg} ${ui.softBorder}`}
        >
          <div className={sectionTitleClass}>🌊 Water Timing</div>
          {waterSignals.length > 0 ? (
            <>
              <div className='mt-2 grid grid-cols-2 gap-2'>
                {waterSignals.map((item) => (
                  <div key={`${item.label}-${item.value}`} className={`rounded-[20px] border px-3 py-3 ${signalToneClasses(item.tone)}`}>
                    <div className='text-[11px] font-black uppercase tracking-[0.08em] opacity-75'>{item.label}</div>
                    <div className='mt-1 text-[14px] font-black'>{item.value}</div>
                    {item.helper ? <div className='mt-1 text-[11px] font-extrabold opacity-70 truncate'>{item.helper}</div> : null}
                  </div>
                ))}
              </div>
              <div className='mt-2'>{renderTideChart(true)}</div>
              {stationLine ? <div className={`mt-1 text-[10px] font-bold ${ui.sub}`}>{stationLine}</div> : null}
            </>
          ) : (
            <div className={`mt-2 rounded-[20px] border px-3 py-3 ${signalToneClasses('sky')}`}>
              <div className='text-[11px] font-black uppercase tracking-[0.08em] opacity-75'>Water timing</div>
              <div className='mt-1 text-[14px] font-black'>Inland conditions mode</div>
              <div className='mt-1 text-[11px] font-extrabold opacity-70'>Using moon, weather, and FishIQ signals instead.</div>
            </div>
          )}
        </button>


        {safeForecast.length > 0 && (
          <div className={`pt-3 border-t ${ui.divider}`}>
            <div className={sectionTitleClass}>📅 Forecast</div>
            <div className='mt-2 grid grid-cols-3 gap-2 text-center'>
              {safeForecast.slice(0, 3).map((day: any, index: number) => {
                const dayKind = kindFor(day?.condition);
                const dayIconClass =
                  dayKind === 'rain' || dayKind === 'storm'
                    ? isDark ? 'text-sky-200' : 'text-blue-700'
                    : dayKind === 'cloud' || dayKind === 'snow'
                      ? isDark ? 'text-indigo-100' : 'text-indigo-700'
                      : isDark ? 'text-yellow-200' : 'text-amber-600';

                return (
                  <button
                    type='button'
                    onClick={() => openDetail({ kind: 'forecastDay', day, index })}
                    key={day.day || index}
                    className={`${detailButtonClass} p-3 text-center ${ui.tileBg} ${ui.tileBorder}`}
                  >
                    <div className={`text-[11px] font-black ${ui.statValue}`}>{day.day}</div>
                    <div className='my-1.5 flex justify-center'>{iconFor(dayKind, `w-6 h-6 ${dayIconClass}`)}</div>
                    <div className={`text-[11px] font-bold ${ui.statLabel}`}>
                      <span className={`font-black ${ui.statValue}`}>{day.high}{tempSymbol}</span> / {day.low}{tempSymbol}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );

  const panel = renderPanelShell(
    <div className={panelCard}>
      {detailPanel ? renderDetailPanel() : overview}
    </div>,
  );

  return (
    <div ref={rootRef} className='relative roddo-weather-polish' data-roddo-polish-lane='2'>
      <button
        type='button'
        onClick={() => onExpandedChange?.(!open)}
        className={pillClass + (open ? ' ring-2 ring-[#2F80ED]/25 dark:ring-[#67D8E8]/25' : '')}
        aria-label='Weather'
        title='Weather'
      >
        <span className='leading-none'>{iconFor(kind, `w-5 h-5 ${iconClass}`)}</span>
        <div className={`mt-0.5 text-[10px] font-black leading-none ${ui.pillText}`}>{temp}°</div>
      </button>
      {open && renderPanel(panel)}
    </div>
  );
};

export default WeatherWidget;
